import { useEffect } from 'react'
import toast from 'react-hot-toast'

import { useFvNavigate } from '@fv/client-components'
import { useQuery, useQueryClient } from '@fv/client-core'
import type { FullShipment } from '@fv/client-types'

import { isProcessingQuotes } from '@/utils/shipmentFuncs'
import { shipperFetch } from '@/utils/shipperFetch'

const REFETCH_INTERVAL = 15000

type UseLoadOpts = {
  refetchWhileQuoting?: boolean
  refetchWhileDispatching?: boolean
  showDiscardedError?: boolean
  enabled?: boolean
}

export const loadKeys = {
  all: ['load'] as const,
  id: (id?: string | null) => [...loadKeys.all, id ?? null] as const,
}

const fetchLoad = (loadId?: string | null) => async () => {
  if (!loadId) throw new Error('`loadId` required to fetch load')
  return shipperFetch<FullShipment>(`/shipments/${loadId}`)
}

export function useLoad(loadId?: string | null, options?: UseLoadOpts) {
  const navigate = useFvNavigate()
  const query = useQuery<FullShipment, any>(
    loadKeys.id(loadId),
    fetchLoad(loadId),
    {
      enabled: Boolean(loadId) && options?.enabled !== false,

      retry(_failureCount, error) {
        return error?.name !== 'DISCARDED' // don't retry if the load is discarded
      },

      refetchInterval: query => {
        const data = query.state.data
        if (options?.refetchWhileQuoting && isProcessingQuotes(data)) {
          return REFETCH_INTERVAL
        }

        if (
          options?.refetchWhileDispatching &&
          data?.pickup?.status === 'requesting'
        ) {
          return REFETCH_INTERVAL
        }

        return false
      },
    },
  )

  useEffect(() => {
    const error = query.error
    if (!error) return

    if (options?.showDiscardedError && error?.name === 'DISCARDED') {
      toast.error(error?.message)
      navigate('/active')
    }
  }, [query.error, navigate, options?.showDiscardedError])

  return query
}

export const useSetLoadQueryData = () => {
  const queryClient = useQueryClient()
  return (loadId: string, data: FullShipment) => {
    queryClient.setQueryData(loadKeys.id(loadId), data)
  }
}
